import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/audio.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import './video-player.css';

import { useRef, useState, useEffect } from 'react';
import { MediaPlayer, MediaProvider, Poster, Track } from '@vidstack/react';
import { DefaultVideoLayout, defaultLayoutIcons } from '@vidstack/react/player/layouts/default';

export interface TextTrack {
  src: string;
  label: string;
  language: string;
  kind: 'subtitles' | 'captions' | 'chapters' | 'descriptions' | 'metadata';
  type?: 'vtt' | 'srt' | 'ass';
  default?: boolean;
}

export interface VideoPlayerProps {
  /** Video source URL */
  src: string;
  /** Video title */
  title?: string;
  /** Poster image URL */
  poster?: string;
  /** Thumbnails VTT file URL for timeline preview */
  thumbnails?: string;
  /** Text tracks (subtitles, captions, chapters) */
  textTracks?: TextTrack[];
  /** Aspect ratio (default: 16/9) */
  aspectRatio?: string;
  /** Stream type */
  streamType?: 'on-demand' | 'live' | 'live:dvr' | 'll-live' | 'll-live:dvr' | 'unknown';
  /** View type */
  viewType?: 'video' | 'audio' | 'unknown';
  /** Enable crossOrigin */
  crossOrigin?: boolean;
  /** Enable playsInline for mobile */
  playsInline?: boolean;
  /** Autoplay */
  autoPlay?: boolean;
  /** Muted */
  muted?: boolean;
  /** Loop */
  loop?: boolean;
  /** Log level for debugging */
  logLevel?: 'silent' | 'error' | 'warn' | 'info' | 'debug';
  /** Custom class name */
  className?: string;
  /** Callback when video can play */
  onCanPlay?: () => void;
  /** Callback when video ends */
  onEnded?: () => void;
  /** Callback on play */
  onPlay?: () => void;
  /** Callback on pause */
  onPause?: () => void;
  /** Callback on time update */
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

/**
 * VideoPlayer - A customizable video player component using vidstack
 * 
 * @example
 * ```tsx
 * <VideoPlayer
 *   src="https://example.com/video.mp4"
 *   title="My Video"
 *   poster="https://example.com/poster.jpg"
 *   thumbnails="https://example.com/thumbnails.vtt"
 *   textTracks={[
 *     { src: '/subs/en.vtt', label: 'English', language: 'en', kind: 'subtitles', default: true },
 *   ]}
 * />
 * ```
 */
export function VideoPlayer({
  src,
  title = 'Video',
  poster,
  thumbnails,
  textTracks = [],
  aspectRatio = '16 / 9',
  streamType = 'on-demand',
  viewType = 'video',
  crossOrigin = true,
  playsInline = true,
  autoPlay = false,
  muted = false,
  loop = false,
  logLevel = 'warn',
  className = '',
  onCanPlay,
  onEnded,
  onPlay,
  onPause,
  onTimeUpdate,
}: VideoPlayerProps) {
  const playerRef = useRef<any>(null);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const updateDuration = () => {
      const dur = player?.state?.duration ?? player?.duration ?? 0;
      if (dur > 0 && dur !== duration) {
        setDuration(dur);
      }
    };

    // Set up a polling interval to check for duration changes
    const interval = setInterval(updateDuration, 500);
    
    return () => clearInterval(interval);
  }, [src, duration]);

  return (
    <div 
      className={`video-player-container ${className}`}
      style={{ '--video-aspect-ratio': aspectRatio } as React.CSSProperties}
    >
      <MediaPlayer
        ref={playerRef}
        src={src}
        viewType={viewType}
        streamType={streamType}
        logLevel={logLevel}
        crossOrigin={crossOrigin ? '' : undefined}
        playsInline={playsInline}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        title={title}
        poster={poster}
        onCanPlay={() => {
          // Update duration when video can play
          if (playerRef.current) {
            const dur = playerRef.current.state?.duration ?? playerRef.current.duration ?? 0;
            if (dur > 0) {
              setDuration(dur);
            }
          }
          onCanPlay?.();
        }}
        onEnded={onEnded}
        onPlay={onPlay}
        onPause={onPause}
        onTimeUpdate={(detail) => {
          if (onTimeUpdate) {
            // Use tracked duration state
            onTimeUpdate(detail.currentTime, duration);
          }
        }}
        onSeek={(time) => {
          // Handle seeking - ensure currentTime is updated
          if (playerRef.current) {
            playerRef.current.currentTime = time;
          }
        }}
      >
        <MediaProvider>
          {poster && <Poster className="vds-poster" />}
          {textTracks.map((track) => (
            <Track
              key={track.src}
              src={track.src}
              label={track.label}
              language={track.language}
              kind={track.kind}
              type={track.type}
              default={track.default}
            />
          ))}
        </MediaProvider>
        <DefaultVideoLayout
          thumbnails={thumbnails}
          icons={defaultLayoutIcons}
        />
      </MediaPlayer>
    </div>
  );
}

export default VideoPlayer;
